import {Component, computed, effect, inject, signal, ChangeDetectionStrategy} from '@angular/core';

import {MatButtonToggle, MatButtonToggleGroup} from '@angular/material/button-toggle';
import {RouterLink} from '@angular/router';
import {catchError, finalize, firstValueFrom, from, of} from 'rxjs';
import {rxResource} from '@angular/core/rxjs-interop';
import {HttpClient} from '@angular/common/http';
import {LocationStateService} from '../../../../common/services/location-state.service';
import {DayDatepickerComponent} from '../datepicker/day-datepicker.component';
import {EnergyChartDebugChartComponent} from './energy-chart-debug-chart.component';
import {AverageEnergyResponse} from '../../../../api/models/average-energy-response';
import {BatteryType, EnergyType} from '../energy-chart/base-chart.component';
import {Api} from '../../../../api/api';
import {getCurrentPeakPowerFromGrid, getEnergySummaries} from '../../../../api/functions';
import {CurrentPeakPowerFromGridResponse} from '../../../../api/models/current-peak-power-from-grid-response';
import {formatDebugStatusValue} from './debug-status-table.helper';
import {
  buildEnergyDebugExportRequestUrl,
  buildEnergyDebugExportSections,
  ExportNodeTable,
  ExportTableSection,
} from './energy-chart-debug-export.helper';

type DebugBatteryMode = Extract<BatteryType, 'power' | 'charge'>;
type ExportTimebucket = '1 second' | '5 minutes' | '15 minutes' | '1 hour' | '1 day' | '1 month';

@Component({
  selector: 'app-energy-chart-debug',
  imports: [MatButtonToggleGroup, MatButtonToggle, RouterLink, DayDatepickerComponent, EnergyChartDebugChartComponent],
  templateUrl: './energy-chart-debug.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../energy-chart/energy-chart.component.css',
})
export class EnergyChartDebugComponent {
  private readonly apiInvoker = inject(Api);
  private readonly httpClient = inject(HttpClient);
  private readonly locationStateService = inject(LocationStateService);

  /** Timezone of the currently selected location; `undefined` uses the runtime default. */
  private get locationTimeZone(): string | undefined {
    return this.locationStateService.currentLocation()?.timezone;
  }
  private get displayDateTimeFormatter(): Intl.DateTimeFormat {
    return new Intl.DateTimeFormat('de-AT', {
      timeZone: this.locationTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }
  private get csvDateTimeFormatter(): Intl.DateTimeFormat {
    return new Intl.DateTimeFormat('sv-SE', {
      timeZone: this.locationTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }
  private get fileDateTimeFormatter(): Intl.DateTimeFormat {
    return new Intl.DateTimeFormat('sv-SE', {
      timeZone: this.locationTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  isLoading = signal<boolean>(false);
  isCurrentPeakPowerLoading = signal<boolean>(false);
  isExportLoading = signal<boolean>(false);
  selectedDate = signal<Date>(new Date());
  selectedEnergyOption = signal<EnergyType>('production');
  selectedBatteryMode = signal<DebugBatteryMode>('power');
  exportRangeInput = signal<string>(this.createDefaultExportRange());
  exportTimebucket = signal<ExportTimebucket>('5 minutes');
  exportResponse = signal<Record<string, unknown> | null>(null);
  exportRequestContext = signal<{from: string; to: string; timebucket: ExportTimebucket} | null>(null);
  exportError = signal<string>('');
  locations = computed(() => this.locationStateService.locations());
  exportSections = computed<ExportTableSection[]>(() => this.buildExportSections(this.exportResponse()));
  exportTimebucketOptions: ExportTimebucket[] = ['5 minutes', '15 minutes', '1 hour', '1 second'];

  params = computed(() => ({
    location_id: this.locationStateService.currentLocation()?.id ?? this.locations()[0]?.id ?? '',
    aggregation: 'day',
    at: this.selectedDate().toISOString(),
  }));

  resource = rxResource({
    params: this.params,
    defaultValue: undefined,
    stream: (loaderParams) => {
      this.isLoading.set(true);
      return from(this.apiInvoker.invoke(getEnergySummaries, loaderParams.params)).pipe(
        catchError((error) => {
          console.error('Daily debug status service is not reachable.', error);
          return of({} as AverageEnergyResponse);
        }),
        finalize(() => this.isLoading.set(false)),
      );
    },
  });

  currentPeakPowerDateRange = computed(() => {
    const selectedDate = this.selectedDate();
    const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    return {
      currentMonthStart: startOfMonth.toISOString(),
      planningStart: selectedDate.toISOString(),
    };
  });

  currentPeakPowerParams = computed(() => {
    const locationId = this.locationStateService.currentLocation()?.id ?? this.locations()[0]?.id;
    if (!locationId) {
      return undefined;
    }

    const range = this.currentPeakPowerDateRange();
    return {
      location_id: locationId,
      current_month_start: range.currentMonthStart,
      planning_start: range.planningStart,
    };
  });

  currentPeakPowerResource = rxResource({
    params: this.currentPeakPowerParams,
    defaultValue: undefined as CurrentPeakPowerFromGridResponse | undefined,
    stream: ({params}) => {
      if (!params) {
        return of(undefined);
      }

      this.isCurrentPeakPowerLoading.set(true);
      return from(this.apiInvoker.invoke(getCurrentPeakPowerFromGrid, params)).pipe(
        catchError((error) => {
          console.error('Current peak power from grid service is not reachable.', error);
          return of({} as CurrentPeakPowerFromGridResponse);
        }),
        finalize(() => this.isCurrentPeakPowerLoading.set(false)),
      );
    },
  });

  onEnergySelectionChange(event: any): void {
    this.selectedEnergyOption.set(event.value as EnergyType);
  }

  onBatterySelectionChange(event: any): void {
    this.selectedBatteryMode.set(event.value as DebugBatteryMode);
  }

  updateDate(newDate: Date): void {
    if (!newDate) {
      return;
    }
    this.selectedDate.set(newDate);
  }

  updateExportRangeInput(value: string): void {
    this.exportRangeInput.set(value);
  }

  updateExportTimebucket(value: string): void {
    this.exportTimebucket.set(value as ExportTimebucket);
  }

  async loadExportResults(): Promise<void> {
    const locationId = this.locationStateService.currentLocation()?.id ?? this.locations()[0]?.id;
    if (!locationId) {
      this.exportError.set('Keine Location ausgewahlt.');
      this.exportResponse.set(null);
      this.exportRequestContext.set(null);
      return;
    }

    const parsedRange = this.parseExportRange(this.exportRangeInput());
    if (!parsedRange) {
      this.exportError.set('Bitte Bereich als from=... und to=... eingeben.');
      this.exportResponse.set(null);
      this.exportRequestContext.set(null);
      return;
    }

    this.isExportLoading.set(true);
    this.exportError.set('');

    try {
      const response = await firstValueFrom(
        this.httpClient.get<Record<string, unknown>>(
          buildEnergyDebugExportRequestUrl(
            this.apiInvoker.rootUrl,
            locationId,
            parsedRange.from,
            parsedRange.to,
            this.exportTimebucket(),
          ),
        ),
      );
      this.exportResponse.set((response as Record<string, unknown>) ?? null);
      this.exportRequestContext.set({
        from: parsedRange.from,
        to: parsedRange.to,
        timebucket: this.exportTimebucket(),
      });
    } catch (error) {
      console.error('Energy debug export service is not reachable.', error);
      this.exportError.set('Export konnte nicht geladen werden.');
      this.exportResponse.set(null);
      this.exportRequestContext.set(null);
    } finally {
      this.isExportLoading.set(false);
    }
  }

  formatPeakPowerValue(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '';
    }
    return value.toFixed(3);
  }

  formatPeakPowerTop3(values: Array<number> | null | undefined): string {
    if (!Array.isArray(values) || values.length === 0) {
      return '';
    }

    return values
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
      .map((value) => value.toFixed(3))
      .join(', ');
  }

  formatExportValue(value: number | null | undefined): string {
    return formatDebugStatusValue(value);
  }

  formatTimestamp(timestamp: string | null | undefined): string {
    return this.formatTimestampInLocationZone(timestamp, 'display');
  }

  exportTableToCsv(table: ExportNodeTable): void {
    if (!table.rows.length) {
      return;
    }

    const csvRows = [
      ['timestamp', 'value'].join(','),
      ...table.rows.map((row) => [
        this.escapeCsvValue(this.formatTimestampForCsv(row.timestamp)),
        this.escapeCsvValue(this.formatExportValue(row.value)),
      ].join(',')),
    ];

    const blob = new Blob([csvRows.join('\n')], {type: 'text/csv;charset=utf-8;'});
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.toFileSafeName(this.buildExportFilenameTitle(table.title))}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  constructor() {
    effect(() => {
      const availableLocations = this.locations();
      const currentLocation = this.locationStateService.currentLocation();
      if (!currentLocation && availableLocations.length > 0) {
        this.locationStateService.setLocation(availableLocations[0]);
      }
    });
  }

  private createDefaultExportRange(referenceDate: Date = new Date()): string {
    const startOfLastMonth = new Date(
      referenceDate.getFullYear(),
      referenceDate.getMonth() - 1,
      1,
      0, 0, 0, 0
    );

    const toOfLastMonth = new Date(
      referenceDate.getFullYear(),
      referenceDate.getMonth(),
      0, // day 0 = last day of previous month
      23, 59, 59, 999
    );

    return `from=${startOfLastMonth.toISOString()}\nto=${toOfLastMonth.toISOString()}`;
  }

  private parseExportRange(input: string): {from: string; to: string} | null {
    const lines = input
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => !!line);

    if (lines.length === 0) {
      return null;
    }

    let from = '';
    let to = '';
    const bareValues: string[] = [];

    for (const line of lines) {
      const [rawKey, ...rest] = line.split('=');
      if (rest.length === 0) {
        bareValues.push(line);
        continue;
      }

      const key = rawKey.trim().toLowerCase();
      const value = rest.join('=').trim();

      if (key === 'from' || key === 'start') {
        from = value;
      }

      if (key === 'to' || key === 'to') {
        to = value;
      }
    }

    if (!from && bareValues[0]) {
      from = bareValues[0];
    }

    if (!to && bareValues[1]) {
      to = bareValues[1];
    }

    if (!from || !to || Number.isNaN(new Date(from).getTime()) || Number.isNaN(new Date(to).getTime())) {
      return null;
    }

    return {from, to};
  }

  private buildExportSections(response: Record<string, unknown> | null): ExportTableSection[] {
    return buildEnergyDebugExportSections(response);
  }

  private escapeCsvValue(value: string): string {
    return `"${value.replace(/"/g, '""')}"`;
  }

  private formatTimestampForCsv(timestamp: string | null | undefined): string {
    return this.formatTimestampInLocationZone(timestamp, 'csv');
  }

  private buildExportFilenameTitle(nodeTitle: string): string {
    const context = this.exportRequestContext();
    if (!context) {
      return nodeTitle || 'export';
    }

    const fromDate = this.formatDateForFileName(context.from);
    const toDate = this.formatDateForFileName(context.to);
    return `${nodeTitle} from ${fromDate} to ${toDate} ${context.timebucket}`;
  }

  private formatDateForFileName(timestamp: string): string {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return timestamp;
    }
    return this.fileDateTimeFormatter
      .format(date)
      .replace(',', '')
      .replace(/ /g, '_')
      .replace(/:/g, '-');
  }

  private toFileSafeName(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'export';
  }

  private formatTimestampInLocationZone(timestamp: string | null | undefined, mode: 'display' | 'csv'): string {
    if (!timestamp) {
      return '';
    }

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return timestamp;
    }

    if (mode === 'csv') {
      return this.csvDateTimeFormatter.format(date).replace(',', '');
    }

    return this.displayDateTimeFormatter.format(date);
  }
}
